import { orderBy } from "lodash";

type CriterionResult = {
  attribute: string;
  result?: boolean;
};

type RankedProduct = {
  lender: string;
  fit: number;
  rate: number;
  criteriaResults: CriterionResult[];
};

type SortOrder = "asc" | "desc";
type OrderByParameters = [keys: string[], orders: SortOrder[]];

type RankingOptions = {
  professionalServices?: string | null;
  criteriaPriority?: string[];
};

// Earlier attributes take precedence when a professional service is entered and "Must match" is empty.
export const DEFAULT_CRITERIA_PRIORITY = ["professionalServices", "loanAmount"];

/**
 * Chooses one product per lender, then orders those products for display.
 * The caller applies lender, product type and "Must match" filters first.
 *
 * With a professional service entered and "Must match" empty, both stages rank by:
 * 1. Professional services match.
 * 2. Loan amount match.
 * 3. Lowest overall fit.
 * 4. Lowest interest rate.
 * Selected "Order by" options break remaining ties in the displayed list.
 * Each priority outranks everything below it, so a professional services match
 * can rank above a product with a better overall fit or rate.
 *
 * If professional services is blank or "Must match" has a selection, preserve
 * the original behaviour: choose each lender's product by fit, then rate, and
 * order the chosen products using only the selected "Order by" options.
 *
 * A priority attribute matches only when it has criteria and all explicitly
 * return true. Missing, unknown and failed criteria receive no match priority;
 * they do not exclude a product here. The supplied fit counts all criteria that
 * did not pass, including unknown results; this function does not recalculate it.
 */
export function rankProducts<T extends RankedProduct>(
  products: T[],
  criteriaFilterValue: string[] | string | null | undefined,
  orderByParametersList: OrderByParameters,
  { professionalServices, criteriaPriority = DEFAULT_CRITERIA_PRIORITY }: RankingOptions = {},
): T[] {
  const hasProfessionalService = typeof professionalServices === "string" && professionalServices.trim().length > 0;
  // Enable the new ranking only when the user entered a professional service and "Must match" is empty.
  // Otherwise, use the original ranking.
  const useCriteriaPriority =
    hasProfessionalService && Array.isArray(criteriaFilterValue) && criteriaFilterValue.length === 0;
  const priorityKeys = useCriteriaPriority
    ? criteriaPriority.map((attribute) => (product: T) => {
        const results = product.criteriaResults.filter((criterion) => criterion.attribute === attribute);
        // Absent, unknown and failed criteria do not receive a matching priority.
        return results.length > 0 && results.every((criterion) => criterion.result === true) ? 0 : 1;
      })
    : [];
  const rankingKeys = [...priorityKeys, "fit", "rate"];
  const rankingOrders = rankingKeys.map((): SortOrder => "asc");

  const groupedList = products.reduce((groups, product) => {
    const lenderProducts = groups.get(product.lender) ?? [];
    lenderProducts.push(product);
    groups.set(product.lender, lenderProducts);
    return groups;
  }, new Map<string, T[]>());

  const bestProducts = Array.from(
    groupedList.values(),
    (lenderProducts) => orderBy(lenderProducts, rankingKeys, rankingOrders)[0],
  );

  const [sortKeys, sortOrders] = orderByParametersList;
  const displayKeys = sortKeys.map((key) => (key === "fees" ? "feesTotal" : key));

  // When priority ranking is enabled, use the same ranking across and within lenders.
  // Otherwise, preserve the selected display sort's usual precedence.
  return useCriteriaPriority
    ? orderBy(bestProducts, [...rankingKeys, ...displayKeys], [...rankingOrders, ...sortOrders])
    : orderBy(bestProducts, displayKeys, sortOrders);
}
